import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Scale, Copyright, AlertTriangle, FileText, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

export default function TermsOfService() {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEO 
        title="Terms of Service - sQawk"
        description="sQawk Terms of Service - Read our terms and conditions for using the sQawk platform."
      />
      
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold">Terms of Service</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Last Updated */}
        <div className="text-slate-400 text-sm">
          Last Updated: February 21, 2026
        </div>

        {/* Introduction */}
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-emerald-400" />
            <h2 className="text-2xl font-bold">Introduction</h2>
          </div>
          <p className="text-slate-300 leading-relaxed">
            Welcome to sQawk ("we," "our," or "us"). By accessing or using our platform at sqawk.app 
            (the "Service"), you agree to be bound by these Terms of Service ("Terms"). Please read 
            them carefully before using our Service.
          </p>
          <p className="text-slate-300 leading-relaxed">
            sQawk is Social Media for People - a next-generation social platform designed for authentic 
            connections and meaningful conversations.
          </p>
        </section>

        {/* Intellectual Property */}
        <section className="space-y-4 bg-slate-900/50 rounded-xl p-6 border border-slate-800">
          <div className="flex items-center gap-3">
            <Copyright className="w-6 h-6 text-amber-400" />
            <h2 className="text-2xl font-bold">Intellectual Property Rights</h2>
          </div>
          <div className="space-y-4 text-slate-300">
            <p className="leading-relaxed">
              <strong className="text-white">All content, features, and functionality</strong> of the sQawk 
              platform—including but not limited to text, graphics, logos, icons, images, audio clips, 
              digital downloads, data compilations, software, and the overall design and appearance—are 
              the exclusive property of sQawk and are protected by United States and international 
              copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-400 mb-2">Prohibited Actions</h3>
                  <ul className="space-y-2 text-sm">
                    <li>• <strong>Copying or reproducing</strong> any part of the sQawk platform, including source code, design elements, or functionality</li>
                    <li>• <strong>Creating derivative works</strong> based on sQawk's design, features, or code</li>
                    <li>• <strong>Reverse engineering, decompiling, or disassembling</strong> any part of the Service</li>
                    <li>• <strong>Scraping, crawling, or harvesting</strong> content or data from the platform</li>
                    <li>• <strong>Removing or altering</strong> any copyright, trademark, or other proprietary notices</li>
                    <li>• <strong>Using automated tools</strong> to access or interact with the Service without authorization</li>
                  </ul>
                </div>
              </div>
            </div>

            <p className="leading-relaxed">
              The sQawk name, logo, and all related names, logos, product and service names, designs, 
              and slogans are trademarks of sQawk or its affiliates. You may not use such marks without 
              our prior written permission.
            </p>
          </div>
        </section>

        {/* Trademark Notice */}
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-blue-400" />
            <h2 className="text-2xl font-bold">Trademark Notice</h2>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-slate-300 leading-relaxed">
              <strong className="text-white">sQawk™</strong> is a trademark of sQawk. The sQawk logo, 
              "Social Media for People" slogan, and the distinctive Q bird design are protected marks. 
              Unauthorized use of these marks is strictly prohibited and may result in legal action.
            </p>
          </div>
        </section>

        {/* User Conduct */}
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <Scale className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-bold">User Conduct</h2>
          </div>
          <p className="text-slate-300 leading-relaxed">
            By using sQawk, you agree to:
          </p>
          <ul className="space-y-2 text-slate-300">
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">✓</span>
              Use the Service only for lawful purposes and in accordance with these Terms
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">✓</span>
              Not impersonate any person or entity or falsely state your affiliation
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">✓</span>
              Not upload content that infringes on intellectual property rights
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">✓</span>
              Not engage in harassment, hate speech, or abusive behavior
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">✓</span>
              Not attempt to gain unauthorized access to our systems
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-400 mt-1">✓</span>
              Respect the privacy and rights of other users
            </li>
          </ul>
        </section>

        {/* User Content */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">User Content</h2>
          <p className="text-slate-300 leading-relaxed">
            You retain ownership of content you post on sQawk. By posting content, you grant us a 
            non-exclusive, worldwide, royalty-free license to use, display, and distribute your 
            content on the platform. You are responsible for ensuring you have the rights to post 
            any content you share.
          </p>
        </section>

        {/* Termination */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Termination</h2>
          <p className="text-slate-300 leading-relaxed">
            We may terminate or suspend your account and access to the Service immediately, without 
            prior notice or liability, for any reason, including breach of these Terms. Upon termination, 
            your right to use the Service will cease immediately.
          </p>
        </section>

        {/* Limitation of Liability */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Limitation of Liability</h2>
          <p className="text-slate-300 leading-relaxed">
            To the maximum extent permitted by law, sQawk shall not be liable for any indirect, 
            incidental, special, consequential, or punitive damages resulting from your use of or 
            inability to use the Service.
          </p>
        </section>

        {/* Changes to Terms */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold">Changes to Terms</h2>
          <p className="text-slate-300 leading-relaxed">
            We reserve the right to modify these Terms at any time. We will notify users of material 
            changes by posting the new Terms on this page and updating the "Last Updated" date. Your 
            continued use of the Service after changes constitutes acceptance of the new Terms.
          </p>
        </section>

        {/* Contact */}
        <section className="space-y-4 bg-slate-900/50 rounded-xl p-6 border border-slate-800">
          <div className="flex items-center gap-3">
            <Mail className="w-6 h-6 text-emerald-400" />
            <h2 className="text-2xl font-bold">Contact Us</h2>
          </div>
          <p className="text-slate-300 leading-relaxed">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="space-y-2 text-slate-300">
            <p>Email: <a href="mailto:hello@sqawk.app" className="text-emerald-400 hover:underline">hello@sqawk.app</a></p>
            <p>Website: <a href="https://sqawk.app" className="text-emerald-400 hover:underline">sqawk.app</a></p>
          </div>
        </section>

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm pt-8 border-t border-slate-800">
          <p>© 2026 sQawk. All rights reserved.</p>
          <p className="mt-2">sQawk™ is a trademark of sQawk.</p>
        </div>
      </div>
    </div>
  );
}
